import { useEffect, useState, type FormEvent } from 'react';
import { AdminLayout } from '../components/admin-layout';
import {
  Card,
  Table,
  Th,
  Td,
  Button,
  Input,
  Badge,
  Pagination,
  Alert,
  Select,
} from '../components/ui';
import { getPegawaiRoles, updatePegawaiRoles } from '../features/pegawai-role/api';
import type { PegawaiRoleRow } from '@app/shared';
import { apiList } from '../lib/api';
import { ApiPath } from '@app/shared';
import { z } from 'zod';

const pegawaiListItemSchema = z.object({
  id: z.number(),
  nama: z.string(),
  nip_nik: z.string().nullable(),
  status_aktif: z.boolean(),
});

type PegawaiListItem = z.infer<typeof pegawaiListItemSchema>;

interface AvailableRole {
  id: number;
  kode: string;
  nama: string;
  status_aktif: boolean;
}

interface RoleAssignment {
  role_id: number;
  tanggal_mulai: string;
  tanggal_selesai: string | null;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function PegawaiRolePage() {
  const [pegawaiList, setPegawaiList] = useState<PegawaiListItem[]>([]);
  const [selectedPegawai, setSelectedPegawai] = useState<number | null>(null);
  const [pegawaiNama, setPegawaiNama] = useState('');
  const [currentRoles, setCurrentRoles] = useState<PegawaiRoleRow[]>([]);
  const [availableRoles, setAvailableRoles] = useState<AvailableRole[]>([]);
  const [assignments, setAssignments] = useState<RoleAssignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pegawaiSearch, setPegawaiSearch] = useState('');
  const [pegawaiPage, setPegawaiPage] = useState(1);
  const [pegawaiMeta, setPegawaiMeta] = useState({ page: 1, total_pages: 1 });

  // Load pegawai list
  useEffect(() => {
    setLoading(true);
    apiList(ApiPath.MASTER.PEGAWAI, pegawaiListItemSchema, {
      page: pegawaiPage,
      limit: 10,
      search: pegawaiSearch || undefined,
      sort_by: 'nama',
      sort_dir: 'asc',
    })
      .then((result) => {
        setPegawaiList(result.rows);
        setPegawaiMeta(result.meta);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Gagal memuat daftar pegawai'))
      .finally(() => setLoading(false));
  }, [pegawaiPage, pegawaiSearch]);

  // Load role assignments for selected pegawai
  useEffect(() => {
    if (!selectedPegawai) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    getPegawaiRoles(selectedPegawai)
      .then((data) => {
        setPegawaiNama(data.pegawai.nama);
        setCurrentRoles(data.roles);
        setAvailableRoles(data.available_roles);
        setAssignments(
          data.roles.map((r) => ({
            role_id: r.role_id,
            tanggal_mulai: r.tanggal_mulai,
            tanggal_selesai: r.tanggal_selesai,
          })),
        );
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Gagal memuat role assignments'))
      .finally(() => setLoading(false));
  }, [selectedPegawai]);

  const handleAddRole = () => {
    setAssignments([...assignments, { role_id: 0, tanggal_mulai: today(), tanggal_selesai: null }]);
  };

  const handleRemoveRole = (index: number) => {
    setAssignments(assignments.filter((_, i) => i !== index));
  };

  const handleRoleIdChange = (index: number, value: number) => {
    const updated = assignments.map((a, i) => (i === index ? { ...a, role_id: value } : a));
    setAssignments(updated);
  };

  const handleTanggalMulaiChange = (index: number, value: string) => {
    const updated = assignments.map((a, i) => (i === index ? { ...a, tanggal_mulai: value } : a));
    setAssignments(updated);
  };

  const handleTanggalSelesaiChange = (index: number, value: string) => {
    const updated = assignments.map((a, i) =>
      i === index ? { ...a, tanggal_selesai: value || null } : a,
    );
    setAssignments(updated);
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedPegawai) return;

    const validAssignments = assignments.filter((a) => a.role_id > 0);
    if (validAssignments.length === 0 && assignments.length > 0) {
      setError('Pilih role untuk setiap assignment atau hapus assignment yang tidak diinginkan');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await updatePegawaiRoles(
        selectedPegawai,
        validAssignments.map((a) => ({
          role_id: a.role_id,
          tanggal_mulai: a.tanggal_mulai,
          tanggal_selesai: a.tanggal_selesai || null,
        })),
      );
      setCurrentRoles(result);
      setSuccess('Role assignments berhasil diperbarui');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal menyimpan role assignments');
    } finally {
      setSaving(false);
    }
  };

  const assignedRoleIds = assignments.map((a) => a.role_id).filter(Boolean);

  return (
    <AdminLayout title="Manajemen Role Pegawai">
      <div style={{ padding: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.5rem' }}>
          Manajemen Role Pegawai
        </h1>

        {error && <Alert type="error">{error}</Alert>}
        {success && <Alert type="success">{success}</Alert>}

        <Card>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>
              Pilih Pegawai
            </label>
            <Input
              placeholder="Cari pegawai..."
              value={pegawaiSearch}
              onChange={(e) => {
                setPegawaiSearch(e.target.value);
                setPegawaiPage(1);
              }}
            />
          </div>
          <Table>
            <thead>
              <tr>
                <Th>Nama</Th>
                <Th>NIP/NIK</Th>
                <Th>Status</Th>
                <Th>Aksi</Th>
              </tr>
            </thead>
            <tbody>
              {pegawaiList.map((p) => (
                <tr key={p.id}>
                  <Td>{p.nama}</Td>
                  <Td>{p.nip_nik || '-'}</Td>
                  <Td>
                    <Badge color={p.status_aktif ? 'green' : 'red'}>
                      {p.status_aktif ? 'Aktif' : 'Nonaktif'}
                    </Badge>
                  </Td>
                  <Td>
                    <Button
                      variant={selectedPegawai === p.id ? 'primary' : 'secondary'}
                      onClick={() => setSelectedPegawai(p.id)}
                    >
                      {selectedPegawai === p.id ? 'Dipilih' : 'Pilih'}
                    </Button>
                  </Td>
                </tr>
              ))}
              {pegawaiList.length === 0 && !loading && (
                <tr>
                  <Td colSpan={4} style={{ textAlign: 'center' }}>
                    Tidak ada pegawai ditemukan
                  </Td>
                </tr>
              )}
            </tbody>
          </Table>
          <Pagination
            page={pegawaiMeta.page}
            totalPages={pegawaiMeta.total_pages}
            onChange={setPegawaiPage}
          />
        </Card>

        {selectedPegawai && (
          <Card style={{ marginTop: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem' }}>
              Role Assignments: {pegawaiNama}
            </h2>

            <form onSubmit={handleSave}>
              <div style={{ marginBottom: '1rem' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>Daftar Role</span>
                  <Button type="button" variant="secondary" onClick={handleAddRole}>
                    + Tambah Role
                  </Button>
                </div>

                {assignments.length === 0 && (
                  <p style={{ color: '#64748B', fontStyle: 'italic' }}>
                    Belum ada role yang ditetapkan. Klik "Tambah Role" untuk memulai.
                  </p>
                )}

                {assignments.map((assignment, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      gap: '0.75rem',
                      alignItems: 'flex-end',
                      marginBottom: '0.75rem',
                      padding: '0.75rem',
                      border: '1px solid #E2E8F0',
                      borderRadius: '0.375rem',
                      flexWrap: 'wrap',
                    }}
                  >
                    <div style={{ minWidth: '200px', flex: 1 }}>
                      <label
                        style={{
                          display: 'block',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          marginBottom: '0.25rem',
                        }}
                      >
                        Role
                      </label>
                      <Select
                        value={assignment.role_id.toString()}
                        onChange={(e) => handleRoleIdChange(index, Number(e.target.value))}
                        required
                      >
                        <option value="">Pilih role</option>
                        {availableRoles
                          .filter(
                            (r) => !assignedRoleIds.includes(r.id) || r.id === assignment.role_id,
                          )
                          .map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.nama} ({r.kode})
                            </option>
                          ))}
                      </Select>
                    </div>
                    <div style={{ minWidth: '160px' }}>
                      <label
                        style={{
                          display: 'block',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          marginBottom: '0.25rem',
                        }}
                      >
                        Tanggal Mulai
                      </label>
                      <Input
                        type="date"
                        value={assignment.tanggal_mulai}
                        onChange={(e) => handleTanggalMulaiChange(index, e.target.value)}
                        required
                      />
                    </div>
                    <div style={{ minWidth: '160px' }}>
                      <label
                        style={{
                          display: 'block',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          marginBottom: '0.25rem',
                        }}
                      >
                        Tanggal Selesai
                      </label>
                      <Input
                        type="date"
                        value={assignment.tanggal_selesai ?? ''}
                        onChange={(e) => handleTanggalSelesaiChange(index, e.target.value)}
                        placeholder="Opsional"
                      />
                    </div>
                    <div style={{ alignSelf: 'flex-end' }}>
                      <Button
                        type="button"
                        variant="danger"
                        onClick={() => handleRemoveRole(index)}
                      >
                        Hapus
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                <Button type="submit" disabled={saving}>
                  {saving ? 'Menyimpan...' : 'Simpan Role Assignments'}
                </Button>
              </div>
            </form>

            {/* Current roles display */}
            {currentRoles.length > 0 && (
              <div style={{ marginTop: '2rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Role Saat Ini
                </h3>
                <Table>
                  <thead>
                    <tr>
                      <Th>Role</Th>
                      <Th>Kode</Th>
                      <Th>Tanggal Mulai</Th>
                      <Th>Tanggal Selesai</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRoles.map((r) => (
                      <tr key={r.id}>
                        <Td>{r.role_nama}</Td>
                        <Td>
                          <Badge color="blue">{r.role_kode}</Badge>
                        </Td>
                        <Td>{r.tanggal_mulai}</Td>
                        <Td>{r.tanggal_selesai ?? '—'}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
